import { callRpc } from './base';

/**
 * Comprehensive data structure for Program Report Card
 * Includes data from all 7 program-level tables
 */
export interface ProgramReportCardData {
  // Program basic info
  program_id: string;
  program_name: string;
  baci_program_code: string;
  awarding_organization: string | null;
  founding_year: number | null;
  ai_instructions: string | null;
  qc_flag_preparation: boolean | null;
  qc_flag_preparation_notes: string | null;

  // Contacts (prod.contacts)
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  physical_address: string | null;
  contact_notes: string | null;
  qc_flag_contacts: boolean | null;
  qc_flag_contacts_notes: string | null;

  // Deadlines (prod.deadlines)
  early_bird_deadline: string | null;
  regular_deadline: string | null;
  final_deadline: string | null;
  winners_announcement_date: string | null;
  announcement_timeline: string | null;
  award_ceremony_date: string | null;
  submission_period: string | null;
  deadline_structure: string | null;
  qc_flag_deadlines: boolean | null;
  qc_flag_deadlines_notes: string | null;

  // Requirements (prod.requirements)
  self_published_accepted: boolean | null;
  isbn_required: boolean | null;
  geographic_restrictions: string | null;
  requirements_summary: string | null;
  accepted_formats: string | null;
  manuscript_requirements: boolean | null;
  deadline_type: string | null;
  publication_date_requirements: string | null;
  publication_years_accepted: string | null;
  qc_flag_requirements: boolean | null;
  qc_flag_requirements_notes: string | null;

  // Fees & Prizes (prod.fees_prizes)
  entry_fee: number | null;
  entry_fee_currency: string | null;
  cost_range: string | null;
  prize_structure: any | null; // JSONB
  prizes_per_category: number | null;
  prize_types: string[] | null;
  total_win_opportunities: number | null;
  has_tiered_prizes: boolean | null;
  primary_currency: string | null;
  extra_benefits: string | null;
  qc_flag_fees_prizes: boolean | null;
  qc_flag_fees_prizes_notes: string | null;

  // Media (prod.media)
  website_url: string | null;
  submission_url: string | null;
  past_winners_url: string | null;
  logo_url: string | null;
  website_screenshot_url: string | null;
  seed_urls: string | null;
  wikipedia_entry: string | null;
  // Specialized URLs discovered by URL-Validator agent
  categories_url: string | null;
  deadlines_url: string | null;
  fees_url: string | null;
  requirements_url: string | null;
  contact_url: string | null;
  guidelines_url: string | null;
  // QC flags
  qc_flag_media: boolean | null;
  qc_flag_media_notes: string | null;

  // Judging (prod.judging)
  number_of_categories: number | null;
  judging_criteria: string | null;
  celebration_type: string | null;
  qc_flag_judging: boolean | null;
  qc_flag_judging_notes: string | null;

  // Program Details (prod.program_details)
  host_country: string | null;
  language_requirements: string | null;
  submission_type: string | null;
  has_audiobook_categories: boolean | null;
  bisac_default_primary_code: string | null;
  bisac_default_secondary_code: string | null;
  bisac_default_tertiary_code: string | null;
  bisac_preferred_edition_year: string | null;
  industry_warnings: string | null;
  qc_flag_program_details: boolean | null;
  qc_flag_program_details_notes: string | null;

  // Vetting (prod.vetting)
  program_vetting_status: string | null;
  vetting_notes: string | null;
  qc_flag_vetting: boolean | null;
  qc_flag_vetting_notes: string | null;

  // Categories (array from prod.categories)
  categories: Array<{
    id: string;
    name: string;
    category_tier_1: string | null;
    category_tier_2_subcategory: string | null;
    category_tier_3_detail: string | null;
    bucket_level_1: string | null;
    bucket_level_2: string | null;
  }>;
  qc_flag_categories: boolean | null;
  qc_flag_categories_notes: string | null;
}

/**
 * Navigation data for alphabetical program browsing
 */
export interface ProgramNavigationData {
  programs: Array<{
    id: string;
    baci_program_code: string;
    name: string;
  }>;
  currentIndex: number;
  total: number;
  hasPrevious: boolean;
  hasNext: boolean;
  previousProgram: { id: string; baci_program_code: string; name: string } | null;
  nextProgram: { id: string; baci_program_code: string; name: string } | null;
}

/**
 * Fetch comprehensive report card data for a program
 * Includes all 7 program-level tables in one call
 */
export const getProgramReportCard = async (
  programCode: string
): Promise<ProgramReportCardData> => {
  return await callRpc<ProgramReportCardData, ProgramReportCardData>(
    'get_program_report_card',
    {
      params: {
        p_baci_code: programCode
      },
      successMeta: (result) => ({
        program_id: result.program_id,
        baci_code: result.baci_program_code
      })
    }
  );
};

/**
 * Get navigation data for alphabetical program browsing
 */
export const getProgramNavigation = async (
  currentBaciCode: string
): Promise<ProgramNavigationData> => {
  return await callRpc<ProgramNavigationData, ProgramNavigationData>(
    'get_program_navigation',
    {
      params: {
        p_current_baci_code: currentBaciCode
      },
      successMeta: (result) => ({
        current_index: result.currentIndex,
        total: result.total
      })
    }
  );
};
